import {gql} from 'graphql-tag'
import {print} from 'graphql'

const requiredUserFragment = gql`
    fragment RequiredUserProperties on User {
        id
        active
        preferredName {
            full
        }
        photoBase64(size: small)
        employeeType
    }
`

const customUserFragment = (input) => {
    try {
        return gql`
            fragment CustomUserProperties on User {
                ${input}
            }
        `
    } catch (err) {
        throw new Error('Invalid customFields Syntax')
    }
}

const requiredGroupFragment = gql`
    fragment RequiredGroupProperties on Group {
        id
        name
        members {
            total
        }
    }
`

const customGroupFragment = (input) => {
    try {
        return gql`
            fragment CustomGroupProperties on Group {
                ${input}
            }
        `
    } catch (err) {
        throw new Error('Invalid customFields Syntax')
    }
}

const requiredApplicationFragment = gql`
    fragment RequiredApplicationProperties on Application {
        id
        name
    }
`

const customApplicationFragment = (input) => {
    try {
        return gql`
            fragment CustomApplicationProperties on Application {
                ${input}
            }
        `
    } catch (err) {
        throw new Error('Invalid customFields Syntax')
    }
}

const requiredEntitlementFragment = gql`
    fragment RequiredEntitlementProperties on Entitlement {
        id
        name
        application {
            id
            name
        }
    }
`

const getUserQuery = (id, customFields, includeInactive) => () => ({
    query: print(gql`
        ${requiredUserFragment}
        ${customFields ? customUserFragment(customFields) : ''}
        query fetchUser($id: String!, $includeInactive: Boolean) {
            user: getUserById(id: $id, includeInactive: $includeInactive) {
                ...RequiredUserProperties
                ${customFields ? '...CustomUserProperties' : ''}
            }
        }
    `),
    variables: {id, includeInactive}
})

const currentUser = () => ({
    query: `
    { 
        getCurrentUser { 
            id 
            preferredName {first, last, middle, full} 
            email 
            employeeType 
        } 
    }`,
    variables: {}
})

const getGroupQuery = (id, customFields) => () => ({
    query: print(gql`
        ${requiredGroupFragment}
        ${customFields ? customGroupFragment(customFields) : ''}
        query fetchGroup($id: String!) {
            group: getGroupById(id: $id) {
                ...RequiredGroupProperties
                ${customFields ? '...CustomGroupProperties' : ''}
            }
        }
    `),
    variables: {id}
})

const getApplicationQuery = (id, customFields) => () => ({
    query: print(gql`
        ${requiredApplicationFragment}
        ${customFields ? customApplicationFragment(customFields) : ''}
        query fetchApplication($id: String!) {
            application: getApplicationById(id: $id) {
                ...RequiredApplicationProperties
                ${customFields ? '...CustomApplicationProperties' : ''}
            }
        }
    `),
    variables: {id}
})

const getEntitlementQuery = (id) => () => ({
    query: print(gql`
        ${requiredEntitlementFragment}
        query fetchEntitlement($id: String!) {
            entitlement: getEntitlementById(id: $id) {
                ...RequiredEntitlementProperties
            }
        }
    `),
    variables: {id}
})

const searchUsers = (input, customFields) => () => ({
    query: print(gql`
        ${requiredUserFragment}
        ${customFields ? customUserFragment(customFields) : ''}
        query searchUsers($input: String!) {
            result: searchUsers(query: $input) {
                count
                total
                users: members {
                    ...RequiredUserProperties
                    ${customFields ? '...CustomUserProperties' : ''}
                }
            }
        }
    `),
    variables: {input}
})

const searchGroups = (input, customFields) => () => ({
    query: print(gql`
        ${requiredGroupFragment}
        ${customFields ? customGroupFragment(customFields) : ''}
        query searchGroups($input: String!) {
                  result: searchGroups(query: $input, searchField: Name) {
                      count
                      total
                      groups {
                          ...RequiredGroupProperties
                          ${customFields ? '...CustomGroupProperties' : ''}
                      }
                  }
              }`),
    variables: {input}
})

const searchApplicationsQuery = (input, offset, limit, customFields) => () => ({
    query: print(gql`
        ${customFields ? customApplicationFragment(customFields) : ''}
        query searchApplications($input: String!, $offset: Int!, $limit: Int!) {
            searchResult: searchApplications(
                query: $input
                searchField: Name
                offset: $offset
                limit: $limit
            ) {
                count
                total
                applications {
                    id
                    name
                    ${customFields ? '...CustomApplicationProperties' : ''}
                }
            }
        }
    `),
    variables: {input, offset, limit}
})

const loadAllGroupsUserIsMemberOf = (customFields) => ({
    query: print(gql`
        ${requiredGroupFragment}
        ${customFields ? customGroupFragment(customFields) : ''}
        query getGroupsForUser{
        result: getCurrentUser {
            groups(filter:MemberOf) {
                ...RequiredGroupProperties
                ${customFields ? '...CustomGroupProperties' : ''}
            }
        }
    }`),
    variables: {}
})

const getGroupsForUser = (input) => (customFields) =>
    input
        ? {
              query: print(gql`
                  ${requiredGroupFragment}
                  ${customFields ? customGroupFragment(customFields) : ''}
                  query getGroupsForUser($input: String) {
                  result: getCurrentUser {
                      groups(filter:MemberOf, nameFilter: $input) {
                          ...RequiredGroupProperties
                          ${customFields ? '...CustomGroupProperties' : ''}
                      }
                  }
              }`),
              variables: {input}
          }
        : loadAllGroupsUserIsMemberOf(customFields)

const getApplicationsOwnedByUserQuery = (input, customFields) => () => ({
    query: print(gql`
        ${requiredApplicationFragment}
        ${customFields ? customApplicationFragment(customFields) : ''}
        query getApplicationsOwnedByUser($input: String) {
            result: getCurrentUser {
                applications(filter:OwnedBy, nameFilter: $input) {
                    ...RequiredApplicationProperties
                    ${customFields ? '...CustomApplicationProperties' : ''}
                }
            }
        }`),
    variables: {input}
})

export {
    currentUser,
    getUserQuery,
    getGroupQuery,
    getApplicationQuery,
    getEntitlementQuery,
    getGroupsForUser,
    searchUsers,
    searchGroups,
    searchApplicationsQuery,
    loadAllGroupsUserIsMemberOf,
    getApplicationsOwnedByUserQuery
}
